import { ButtonVariant, Theme } from "./Theme";

type ButtonPalette = { fill: string; line: string; shadow: string; text: string };

const BUTTON_PALETTES: Record<ButtonVariant, ButtonPalette> = {
    red: { fill: Theme.red, line: "#FFD37B", shadow: "#7E251B", text: "#FFF5C7" },
    green: { fill: Theme.green, line: "#F7DD71", shadow: Theme.greenDark, text: "#FFF8D8" },
    blue: { fill: Theme.blue, line: "#F9DA82", shadow: Theme.blueDark, text: "#FFF8D8" },
    gold: { fill: Theme.orange, line: "#FFE18A", shadow: "#9C4C17", text: "#692710" },
    cream: { fill: "#FFE4AD", line: "#C77732", shadow: "#8E3E1D", text: Theme.ink },
    dark: { fill: "#6D6F70", line: "#F6D49B", shadow: "#3D2C27", text: "#FFF4D4" },
};

export class UIFactory {
    static label(
        text: string,
        fontSize = 28,
        color = Theme.ink,
        width = 200,
        height = 50,
        bold = true,
    ): Laya.Label {
        const label = new Laya.Label(text);
        label.width = width;
        label.height = height;
        label.font = "Microsoft YaHei";
        label.fontSize = fontSize;
        label.color = color;
        label.bold = bold;
        label.align = "center";
        label.valign = "middle";
        return label;
    }

    static button(
        text: string,
        width: number,
        height: number,
        onClick: () => void,
        variant: ButtonVariant = "red",
        fontSize = 32,
    ): Laya.Box {
        const box = new Laya.Box();
        box.width = width;
        box.height = height + 9;
        box.mouseEnabled = true;
        box.name = `btn_${text}`;
        this.paintButton(box, width, height, variant);

        const label = this.label(text, fontSize, BUTTON_PALETTES[variant].text, width, height, true);
        label.mouseEnabled = false;
        box.addChild(label);

        box.on(Laya.Event.MOUSE_DOWN, box, () => {
            Laya.Tween.to(box, { scaleX: 0.96, scaleY: 0.96 }, 70, Laya.Ease.quadOut);
        });
        const release = () => Laya.Tween.to(box, { scaleX: 1, scaleY: 1 }, 90, Laya.Ease.backOut);
        box.on(Laya.Event.MOUSE_UP, box, release);
        box.on(Laya.Event.MOUSE_OUT, box, release);
        box.on(Laya.Event.CLICK, box, onClick);
        return box;
    }

    static paintButton(box: Laya.Box, width: number, height: number, variant: ButtonVariant): void {
        const p = BUTTON_PALETTES[variant];
        box.graphics.clear();
        this.roundRect(box, 0, 8, width, height, 22, p.shadow, "#5D2517", 3);
        this.roundRect(box, 0, 0, width, height, 22, p.fill, p.line, 5);
        this.roundRect(box, 10, 8, width - 20, height - 18, 15, p.fill, "#FFFFFF66", 2);
        box.graphics.drawCircle(18, height / 2, 4, p.line);
        box.graphics.drawCircle(width - 18, height / 2, 4, p.line);
    }

    static panel(width: number, height: number, title?: string): Laya.Box {
        const panel = new Laya.Box();
        panel.width = width;
        panel.height = height;
        this.roundRect(panel, 0, 10, width, height - 10, 22, "#9A321F55", null);
        this.roundRect(panel, 0, 0, width, height - 10, 22, Theme.parchment, Theme.redDark, 5);
        this.roundRect(panel, 11, 11, width - 22, height - 32, 15, Theme.parchment, "#D8A56D", 2);
        this.corner(panel, 18, 18, 1, 1);
        this.corner(panel, width - 18, 18, -1, 1);
        this.corner(panel, 18, height - 38, 1, -1);
        this.corner(panel, width - 18, height - 38, -1, -1);
        if (title) {
            const ribbon = this.titleRibbon(title, Math.min(420, width - 80));
            ribbon.pos((width - ribbon.width) / 2, -34);
            panel.addChild(ribbon);
        }
        return panel;
    }

    static titleRibbon(text: string, width = 430): Laya.Box {
        const box = new Laya.Box();
        box.width = width;
        box.height = 84;
        box.graphics.drawPoly(0, 18, [0, 12, 38, 12, 54, 0, width - 54, 0, width - 38, 12, width, 12, width - 18, 42, width, 72, width - 46, 72, width - 60, 84, 60, 84, 46, 72, 0, 72, 18, 42], Theme.red, Theme.goldDark, 4);
        this.roundRect(box, 44, 10, width - 88, 64, 16, "#FFF0C0", "#F2B55A", 3);
        const label = this.label(text, 38, Theme.ink, width - 88, 64, true);
        label.pos(44, 10);
        box.addChild(label);
        return box;
    }

    static statPill(icon: string, value: string, width = 168): Laya.Box {
        const box = new Laya.Box();
        box.width = width;
        box.height = 54;
        this.roundRect(box, 16, 5, width - 16, 44, 22, "#173F4DCC", "#F2CD76", 2);
        box.graphics.drawCircle(26, 27, 24, Theme.gold, Theme.goldDark, 3);
        const iconLabel = this.label(icon, 24, Theme.ink, 48, 48, true);
        iconLabel.pos(2, 3);
        box.addChild(iconLabel);
        const valueLabel = this.label(value, 24, "#FFF5C7", width - 50, 44, true);
        valueLabel.name = "value";
        valueLabel.pos(47, 5);
        box.addChild(valueLabel);
        return box;
    }

    static backButton(onClick: () => void): Laya.Box {
        const box = new Laya.Box();
        box.width = 72;
        box.height = 72;
        box.mouseEnabled = true;
        box.graphics.drawCircle(36, 39, 32, "#9E4B22", "#6D2D19", 3);
        box.graphics.drawCircle(36, 34, 31, "#FFC15A", "#FFF0AC", 3);
        box.graphics.drawPoly(20, 34, [17, 0, 35, -16, 43, -8, 32, 0, 43, 8, 35, 16], Theme.ink);
        box.on(Laya.Event.CLICK, box, onClick);
        return box;
    }

    static progress(width: number, value: number, color = Theme.green): Laya.Box {
        const box = new Laya.Box();
        box.width = width;
        box.height = 34;
        this.roundRect(box, 0, 0, width, 32, 16, "#6F2A25", "#F2C06D", 4);
        const fillWidth = Math.max(24, Math.min(width - 10, (width - 10) * value));
        this.roundRect(box, 5, 5, fillWidth, 22, 11, color, "#A9ED70", 2);
        return box;
    }

    static stars(count: number, size = 30): Laya.Box {
        const box = new Laya.Box();
        box.width = size * 3;
        box.height = size + 8;
        for (let i = 0; i < 3; i++) {
            const star = this.label("★", size, i < count ? Theme.gold : "#8F8177", size, size + 6, true);
            star.stroke = 2;
            star.strokeColor = i < count ? Theme.goldDark : "#5D4D46";
            star.pos(i * size, 0);
            box.addChild(star);
        }
        return box;
    }

    static setPillValue(pill: Laya.Box, value: string): void {
        const label = pill.getChildByName("value") as Laya.Label;
        if (label) label.text = value;
    }

    static roundRect(
        target: Laya.Sprite,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
        fill: string,
        line: string | null = null,
        lineWidth = 1,
    ): void {
        target.graphics.drawRoundRect(x, y, width, height, radius, radius, radius, radius, fill, line || undefined, lineWidth);
    }

    private static corner(target: Laya.Sprite, x: number, y: number, sx: number, sy: number): void {
        const points = [0, 24 * sy, 0, 0, 24 * sx, 0, 24 * sx, 6 * sy, 7 * sx, 6 * sy, 7 * sx, 24 * sy];
        target.graphics.drawLines(x, y, points, "#C68E57", 3);
    }
}
